import React from 'react';

import uniqueId from 'lodash/uniqueId';

import { A11YLiveMessage } from '@lumapps/aria-live/components/A11YLiveMessage';
import { useSearchResultsLiveStatusMessageText } from '@lumapps/aria-live/hooks/useSearchResultsLiveStatusMessageText';
import { classnames, margin, padding, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { SearchField, SearchFieldProps } from '@lumapps/lumx-filter-and-sort/components/SearchField';
import './index.scss';
/* eslint-disable import/order */
import { EmptyState } from '@lumapps/lumx-states/components/EmptyState';
import { NoResultsState } from '@lumapps/lumx-states/components/NoResultsState';
import { ServiceNotAvailableState } from '@lumapps/lumx-states/components/ServiceNotAvailableState';
import { WarningState } from '@lumapps/lumx-states/components/WarningState';
import { mdiClose, mdiCloseCircle, mdiImageBrokenVariant } from '@lumapps/lumx/icons';
import {
    AlertDialog,
    Button,
    ButtonProps,
    Dialog,
    DialogProps,
    Emphasis,
    FlexBox,
    Heading,
    IconButton,
    Orientation,
    Size,
    Text,
    Toolbar,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useInfiniteScroll } from '@lumapps/lumx-hooks/hooks/useInfiniteScroll';
import { PickerList, PickerListProps } from '../PickerList';
/* eslint-enable import/order */

export const CLASSNAME = 'picker-dialog';

export interface PickerDialogProps<I>
    extends Pick<
        DialogProps,
        'isOpen' | 'size' | 'isLoading' | 'onVisibilityChange' | 'parentElement' | 'preventAutoClose'
    > {
    /** Node to display at the end of the header */
    afterHeader?: React.ReactNode;
    /** Picker list loading state. */
    isListLoading?: boolean;
    /** ReactNode to display at the end of the picker list. */
    afterList?: React.ReactNode;
    /** Picker dialog title. */
    title: string;
    /** Items to pick (must have an id). */
    items?: Array<I>;
    /** Items already selected on picker init */
    initSelectedItems?: I[];
    /** Item key selector. */
    itemKeySelector(item: I): string | number;
    /** Item rendered. */
    itemRenderer: React.FC<I>;
    /** Mark item as navigable */
    itemNavigableSelector?: PickerListProps<I>['itemNavigableSelector'];
    /** Mark item as interactable (can be selectable or navigable) */
    itemSelectableSelector?: PickerListProps<I>['itemSelectableSelector'];
    /** Callback on item navigated */
    onNavigate?: PickerListProps<I>['onNavigate'];
    /** Whether the multiple selection is allowed */
    isMultipleSelect?: boolean;
    /** Search text. */
    searchValue?: string;
    /** Search text placeholder. */
    searchPlaceholder?: string;
    /** Lokalise key for "N selected items" label to go in the clear button (if multiple select is enabled) */
    selectedItemsLabelKey?: string;
    /** Override the picker dialog content. */
    children?: React.ReactNode;
    /** CSS class name. */
    className?: string;
    /** Additional filter fields to add to the right of the search field. */
    additionalFilterFields?: React.ReactNode;
    /** Cancel button props. */
    cancelButtonProps?: ButtonProps;
    /** Confirm button props. */
    confirmButtonProps?: ButtonProps;
    /** additional props for the search field component */
    searchFieldProps?: Partial<SearchFieldProps>;
    /** Reset list selection and keyboard nav on items change. */
    resetListOnItemsChange?: boolean;
    /** Should we displayed an alert modal when the user click away */
    shouldPreventClose?: boolean;
    /** Is there an error */
    hasError?: boolean;
    /** should the picker display an empty state instead of a no result */
    shouldDisplayEmptyState?: boolean;
    /** Label displayed when in empty state (no result with no filter if it is enabled) */
    emptyStateLabel?: string;
    /** callback when clear button is clicked on no results with filter applied (query or filters) */
    onNoResultsClear?(): void;
    /** On picker list scroll end. */
    onScrollEnd?(): void;
    /** On selection canceled. */
    onSelectionCancel?(): void;
    /** Search text change. */
    onSearchChange?(newValue: string): void;
    /** On selection confirmed. */
    onSelectionConfirm(items: I[]): void;
    /** Reloading callback in cas of error */
    onReload?(): void;
}

/**
 * Generic dialog for displaying a list of entities from which to pick from.
 *
 * @family Dialog
 * @param PickerDialogProps
 * @returns PickerDialog
 */
export const PickerDialog = <I,>(props: PickerDialogProps<I>) => {
    const {
        additionalFilterFields,
        afterHeader,
        afterList,
        cancelButtonProps,
        className,
        confirmButtonProps,
        isOpen,
        isListLoading,
        isLoading,
        shouldDisplayEmptyState,
        initSelectedItems,
        items,
        itemKeySelector,
        itemRenderer,
        onNoResultsClear,
        onSelectionCancel,
        onSelectionConfirm,
        onScrollEnd,
        onSearchChange,
        onVisibilityChange,
        parentElement,
        searchPlaceholder,
        searchValue,
        size = Size.regular,
        title,
        resetListOnItemsChange,
        shouldPreventClose = true,
        hasError = false,
        isMultipleSelect = false,
        selectedItemsLabelKey,
        emptyStateLabel,
        searchFieldProps = {},
        onReload,
        itemNavigableSelector,
        itemSelectableSelector,
        onNavigate,
        ...rest
    } = props;
    const { translateKey, translateAndReplace, pluralize } = useTranslate();
    const clearSearch = React.useCallback(() => onSearchChange?.(''), [onSearchChange]);
    const [selectedItems, setSelectedItems] = React.useState<I[]>([]);
    const { get: getDataAttributes } = useDataAttributes('picker-dialog');
    const { block, element } = useClassnames(CLASSNAME);
    const [isAlertDialogOpen, setAlertDialogOpen] = React.useState(false);

    const confirmDialogId = React.useMemo(() => uniqueId(`${CLASSNAME}-confirm-dialog-`), []);
    const closeAlertDialog = React.useCallback(() => setAlertDialogOpen(false), [setAlertDialogOpen]);
    const openAlertDialog = React.useCallback(() => setAlertDialogOpen(!!isOpen), [setAlertDialogOpen, isOpen]);
    const handleCancel = React.useCallback(() => {
        closeAlertDialog();
        if (onSelectionCancel) {
            onSelectionCancel();
        }
    }, [closeAlertDialog, onSelectionCancel]);

    const handleConfirm = React.useCallback(() => {
        closeAlertDialog();
        onSelectionConfirm(selectedItems);
    }, [closeAlertDialog, onSelectionConfirm, selectedItems]);

    const handleVisibilityChange = React.useCallback(
        (isVisible: boolean) => {
            onVisibilityChange?.(isVisible);

            // Reset state after close finished
            if (!isVisible) {
                setSelectedItems([]);
                clearSearch();
            }
        },
        [clearSearch, onVisibilityChange],
    );

    const onDialogClose = () => {
        if (shouldPreventClose) {
            openAlertDialog();
        } else {
            handleCancel();
        }
    };

    const contentRef = React.useRef(null);
    // eslint-disable-next-line deprecation/deprecation
    useInfiniteScroll(contentRef, !items?.length || isListLoading ? undefined : onScrollEnd);

    const searchInputRef = React.useRef(null);
    const labelId = React.useMemo(() => uniqueId(`${CLASSNAME}-label-`), []);

    const selectedItemsLabel = React.useMemo(
        () =>
            selectedItemsLabelKey
                ? translateAndReplace(pluralize(selectedItemsLabelKey, selectedItems.length), {
                      COUNT: selectedItems.length,
                      NB: selectedItems.length,
                  })
                : null,
        [pluralize, selectedItems.length, selectedItemsLabelKey, translateAndReplace],
    );

    // Key used to force reset of the picker list component tree.
    const listKey = React.useRef('');
    React.useEffect(() => {
        if (items && resetListOnItemsChange) {
            setSelectedItems([]);
            // Generating a new key.
            listKey.current = resetListOnItemsChange ? uniqueId() : '';
        }
    }, [items, resetListOnItemsChange]);

    const hasNoResult = (!items || items.length === 0) && !isListLoading && !hasError;

    const isEmpty = hasNoResult && shouldDisplayEmptyState && emptyStateLabel;

    // Using local `A11YLiveMessage` instead of global one because it can't work in a dialog
    const rawLiveStatusMessage = useSearchResultsLiveStatusMessageText({
        query: searchValue,
        displayedResultsLength: items?.length ?? 0,
        isLoading: isListLoading,
        hasError,
    });
    // Deferred so that fast-resolving loading states don't get announced
    const liveStatusMessage = React.useDeferredValue(rawLiveStatusMessage);

    return (
        <>
            <Dialog
                className={block([className])}
                dialogProps={{ 'aria-labelledby': labelId }}
                forceHeaderDivider
                parentElement={parentElement}
                focusElement={searchInputRef}
                contentRef={contentRef}
                isOpen={isOpen}
                isLoading={isLoading}
                size={size}
                onVisibilityChange={handleVisibilityChange}
                onClose={onDialogClose}
                {...rest}
            >
                <header>
                    <Toolbar
                        label={
                            <Heading as="h1" typography="title" id={labelId}>
                                {title}
                            </Heading>
                        }
                        after={
                            <IconButton
                                {...getDataAttributes({ element: 'button', action: 'close' })}
                                emphasis={Emphasis.low}
                                icon={mdiClose}
                                onClick={handleCancel}
                                label={translateKey(GLOBAL.CLOSE)}
                            />
                        }
                    />
                    <FlexBox
                        orientation={Orientation.horizontal}
                        gap={Size.regular}
                        className={classnames(padding('horizontal', 'huge'), margin('bottom', 'big'))}
                        wrap
                    >
                        <SearchField
                            label={searchPlaceholder || translateKey(GLOBAL.SEARCH)}
                            value={searchValue}
                            onSearch={onSearchChange as any}
                            isCompact
                            debounced
                            textFieldProps={{
                                inputRef: searchInputRef,
                                placeholder: searchPlaceholder,
                                'aria-label': searchPlaceholder,
                                isDisabled: Boolean(isEmpty),
                                ...getDataAttributes({ element: 'search-input' }),
                            }}
                            wrapperProps={{
                                className: element('search-field'),
                            }}
                            {...searchFieldProps}
                        />
                        {additionalFilterFields && (
                            <FlexBox orientation={Orientation.horizontal} gap={Size.regular} fillSpace>
                                {additionalFilterFields}
                            </FlexBox>
                        )}
                    </FlexBox>

                    {afterHeader}
                </header>

                <A11YLiveMessage hidden atomic role="status">
                    {liveStatusMessage}
                </A11YLiveMessage>

                {Boolean(hasError && onReload) && (
                    <ServiceNotAvailableState onRetry={onReload} className={margin('vertical', 'huge')} />
                )}
                {Boolean(hasError && !onReload) && (
                    <WarningState
                        icon={mdiImageBrokenVariant}
                        title={translateKey(GLOBAL.GENERIC_ERROR)}
                        className={margin('vertical', 'huge')}
                    />
                )}
                {Boolean(hasNoResult && !shouldDisplayEmptyState && !onNoResultsClear) && (
                    <EmptyState
                        title={translateKey(GLOBAL.NO_RESULTS_FOUND)}
                        message={translateKey(GLOBAL.TRY_ANOTHER_QUERY_OR_FILTER)}
                        isVerticallyCentered={false}
                    />
                )}
                {Boolean(hasNoResult && !shouldDisplayEmptyState && onNoResultsClear) && (
                    <NoResultsState searchValue={searchValue} message={emptyStateLabel} onClear={onNoResultsClear} />
                )}
                {isEmpty && (
                    <EmptyState message={emptyStateLabel} messageProps={{ as: 'span' }} isVerticallyCentered={false} />
                )}
                {Boolean(!hasError && ((items && items.length) || isListLoading)) && (
                    <PickerList<I>
                        key={listKey.current}
                        aria-labelledby={labelId}
                        items={items as I[]}
                        itemRenderer={itemRenderer as any}
                        itemKeySelector={itemKeySelector}
                        itemNavigableSelector={itemNavigableSelector}
                        itemSelectableSelector={itemSelectableSelector}
                        onNavigate={onNavigate}
                        afterList={afterList}
                        alreadyUsedItems={initSelectedItems}
                        disabledItems={initSelectedItems}
                        {...(isMultipleSelect
                            ? {
                                  isMultiple: true,
                                  selectedItems: selectedItems as I[],
                                  setSelectedItems: setSelectedItems as (items: I[]) => void,
                              }
                            : {
                                  selectedItem: selectedItems[0] as I,
                                  setSelectedItem: (item: I): void => setSelectedItems([item]),
                              })}
                    />
                )}
                <footer>
                    <Toolbar
                        label={
                            Boolean(selectedItemsLabel && isMultipleSelect && selectedItems.length) && (
                                <FlexBox orientation={Orientation.horizontal} gap="regular" hAlign="center">
                                    <Text as="p" typography="subtitle1">
                                        {selectedItemsLabel}
                                    </Text>

                                    <Button
                                        {...getDataAttributes({ element: 'button', action: 'clear' })}
                                        emphasis={Emphasis.low}
                                        onClick={() => setSelectedItems([])}
                                        rightIcon={mdiCloseCircle}
                                    >
                                        {translateKey(GLOBAL.CLEAR)}
                                    </Button>
                                </FlexBox>
                            )
                        }
                        after={
                            <FlexBox orientation={Orientation.horizontal} gap="regular">
                                <Button
                                    emphasis={Emphasis.medium}
                                    {...getDataAttributes({ element: 'button', action: 'cancel' })}
                                    {...cancelButtonProps}
                                    className={element('cancel-button', [confirmButtonProps?.className])}
                                    onClick={handleCancel}
                                >
                                    {cancelButtonProps?.children || translateKey(GLOBAL.CANCEL)}
                                </Button>
                                <Button
                                    {...getDataAttributes({ element: 'button', action: 'confirm' })}
                                    {...confirmButtonProps}
                                    className={element('confirm-button', [confirmButtonProps?.className])}
                                    onClick={handleConfirm}
                                    isDisabled={!selectedItems.length || confirmButtonProps?.isDisabled}
                                    tabIndex={!selectedItems.length ? -1 : 0}
                                >
                                    {confirmButtonProps?.children || translateKey(GLOBAL.OK)}
                                </Button>
                            </FlexBox>
                        }
                    />
                </footer>
            </Dialog>
            <AlertDialog
                id={confirmDialogId}
                title={translateKey(GLOBAL.CONFIRM_CLOSE)}
                kind="warning"
                isOpen={isOpen && isAlertDialogOpen}
                onClose={closeAlertDialog}
                cancelProps={{
                    ...getDataAttributes({ element: 'button', action: 'cancel-close' }),
                    onClick: closeAlertDialog,
                    label: translateKey(GLOBAL.CANCEL),
                }}
                confirmProps={{
                    ...getDataAttributes({ element: 'button', action: 'confirm-close' }),
                    onClick: handleCancel,
                    label: translateKey(GLOBAL.OK),
                }}
            >
                <Text as="p" typography="body1">
                    {translateKey(GLOBAL.CONFIRM_CLOSE_DESCRIPTION)}
                </Text>
            </AlertDialog>
        </>
    );
};
